// Shared primitives for the Assessment tabs (src/pages/assessment/*).
// Pure code-motion out of the former Assessment.js god file (Tech Debt
// 2026-09 "Assessment and Backtest are god files" ticket) -- no behaviour
// change, byte-identical logic. These are used by 2+ tabs (CalendarTab,
// MonteCarloTab, and the win_rate/take_profit/windows/returns/excursion/ic/
// pearson blocks still inline in Assessment.js), which is why they live here
// instead of moving into a single tab file.
import React from 'react';
import { winRateColor } from '../../utils/colors';

export const cacheBustBucket = () => String(Math.floor(Date.now() / 30000));

// Bucket-side sets — used by BucketPill (below) plus the assessment fetch's
// derived-state filters in AssessmentDataContext.js. Defined here (not in
// the context module) so BucketPill doesn't need a context->shared->context
// import cycle.
export const CALL_BUCKETS = new Set(['95+', '90+', '85+', '80+', '75+', '70+']);
export const PUT_BUCKETS  = new Set(['<25', '<20', '<15', '<10', '<5']);

// Same reasoning: assessment period lists are used by WinRateTable /
// RtrWinRateTable / RtrPearsonTable / IcTable here AND by
// AssessmentDataContext.js's RTR-correlation derivation.
export const ASSESSMENT_PERIODS = ['1d', '3d', '5d', '7d', '15d', '30d', '60d', '90d'];
export const IC_PERIODS = ASSESSMENT_PERIODS;

// ── Calendar helpers ──────────────────────────────────────────────────────────

export const CALL_BE = 56.3;  // break-even TP rate for calls
export const PUT_BE  = 43.5;  // break-even TP rate for puts

// For the "combined" (All) view, BE depends on the call/put mix in each row.
// A single fixed threshold mislabels rows where both sides individually clear
// their own BE but the blended tp_rate lands between CALL_BE and PUT_BE.
export function blendedBE(row) {
  if (!row) return CALL_BE;
  const cn = row.call_n || 0;
  const pn = row.put_n  || 0;
  if (cn + pn === 0) return CALL_BE;
  if (pn === 0)      return CALL_BE;
  if (cn === 0)      return PUT_BE;
  return (cn * CALL_BE + pn * PUT_BE) / (cn + pn);
}

export const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function heatColor(tp, side, row) {
  if (tp == null) return { bg: 'bg-white/[0.02]', text: 'text-gray-700' };
  const be = side === 'put'  ? PUT_BE
           : side === 'call' ? CALL_BE
           : blendedBE(row);
  const delta = tp - be;
  if (tp >= 75)                  return { bg: 'bg-trading-green-400/25', text: 'text-trading-green-200' };
  if (tp >= 65)                  return { bg: 'bg-trading-green-400/15', text: 'text-trading-green-300' };
  if (delta >= 5)                return { bg: 'bg-trading-green-400/10', text: 'text-trading-green-400' };
  if (delta >= 0)                return { bg: 'bg-white/[0.04]',         text: 'text-gray-300' };
  if (delta >= -8)               return { bg: 'bg-amber-400/8',          text: 'text-amber-300' };
  return                                { bg: 'bg-trading-red-400/12',   text: 'text-trading-red-400' };
}

export function fmtReturn(pct) {
  if (pct == null) return '—';
  return fmtSignedMagnitude(pct, {
    decimals: 0,
    suffix: '%',
    compactSuffix: 'k',
    forcePlusZero: true,
  }) || '—';
}

// ── Delta helpers (for historical-version comparison) ──
export const COMPACT_METRIC_THRESHOLD = 10000;
export const SCIENTIFIC_METRIC_THRESHOLD = 1000000;

export function fmtSignedMagnitude(
  value,
  {
    decimals = 1,
    suffix = '',
    compactSuffix = null,
    compactThreshold = COMPACT_METRIC_THRESHOLD,
    scientificThreshold = SCIENTIFIC_METRIC_THRESHOLD,
    forcePlusZero = false,
  } = {}
) {
  if (value == null || !isFinite(value)) return null;
  const abs = Math.abs(value);
  const sign = value > 0 ? '+' : value < 0 ? '-' : (forcePlusZero ? '+' : '');

  if (abs >= scientificThreshold) {
    return `${sign}${abs.toExponential(2)}${suffix}`;
  }

  if (compactSuffix && abs >= compactThreshold) {
    const scaled = abs / 1000;
    const compactDecimals = decimals === 0 || scaled >= 100 ? 0 : 1;
    return `${sign}${scaled.toFixed(compactDecimals)}${compactSuffix}${suffix}`;
  }

  const regularDecimals = abs >= 100 ? 0 : decimals;
  return `${sign}${abs.toFixed(regularDecimals)}${suffix}`;
}

export function fmtPpDelta(v) {
  return fmtSignedMagnitude(v, { decimals: 1, suffix: 'pp', compactSuffix: 'k' });
}

export function fmtReturnDelta(v) {
  return fmtSignedMagnitude(v, { decimals: 0, suffix: 'pp', compactSuffix: 'k' });
}

export function deltaColor(v, invert = false) {
  if (v == null || !isFinite(v) || v === 0) return 'text-gray-500';
  const good = invert ? v < 0 : v > 0;
  return good ? 'text-trading-green-400' : 'text-trading-red-400';
}

/**
 * Inline delta chip shown next to a value when a historical version is being
 * compared against the active version. `kind`: 'pp' (percentage-point, default)
 * or 'return' (large-magnitude return diff). `invert=true` for metrics where
 * lower is better (e.g. drawdown).
 */
export function Delta({ value, kind = 'pp', invert = false, className = '' }) {
  if (value == null || !isFinite(value)) return null;
  const label = kind === 'return' ? fmtReturnDelta(value) : fmtPpDelta(value);
  if (label == null) return null;
  return (
    <span className={`text-[10px] tabular-nums font-mono ${deltaColor(value, invert)} ${className}`}>
      {label}
    </span>
  );
}

export function returnColor(v) {
  if (v == null) return 'text-gray-600';
  if (v > 2)  return 'text-trading-green-300';
  if (v > 0)  return 'text-trading-green-400';
  if (v > -1) return 'text-amber-300';
  return 'text-trading-red-400';
}

// ── Win Rate / TP% / RTR table primitives ────────────────────────────────────
// Shared by 2+ of the win_rate / take_profit / windows / returns / excursion /
// ic / pearson tabs. Moved verbatim out of Assessment.js.

// Sample-count floor: below this, WR/return cells render `—` regardless of value.
// Single-digit buckets are statistically meaningless and mislead at a glance.
export const MIN_RELIABLE_N = 10;

export function fmtPct(v, decimals = 1) {
  if (v == null) return '—';
  return fmtSignedMagnitude(v, { decimals, suffix: '%', compactSuffix: 'k' }) || '—';
}

// ── Take Profit % thresholds (Phase 17, 2026-04-29) ──
// Option-aligned barriers from shipped Phase H5/15B C1 (TP=+0.35/SL=−0.30 calls,
// TP=+0.35/SL=−0.20 puts). Realistic-slippage break-even per side:
//   Call: net_TP=+0.34 / net_SL=−0.323 → BE = 0.323 / 0.663 ≈ 48.7%
//   Put:  net_TP=+0.34 / net_SL=−0.223 → BE = 0.223 / 0.563 ≈ 39.6%
// Identical % across 30 DTE and 15 DTE because TP/SL ratios are preserved.
export const TP_CALL_BE = 48.7;
export const TP_PUT_BE  = 39.6;

// Take Profit % color: green when comfortably above side BE, amber near BE,
// red when below BE. Side-specific thresholds (Phase 17).
export function tpRateColor(v, side = 'call') {
  if (v == null) return 'text-gray-600';
  const be = side === 'put' ? TP_PUT_BE : TP_CALL_BE;
  const margin = v - be;
  if (margin >= 20) return 'text-trading-green-300 font-semibold';
  if (margin >= 10) return 'text-trading-green-400 font-medium';
  if (margin >=  0) return 'text-trading-green-400';
  if (margin >= -8) return 'text-amber-300';
  return 'text-trading-red-400';
}

// Amber-glow box-shadow matching ScoreBadge's super-high treatment. Ramps 90→100.
export const WINRATE_GLOW_RGB = [198, 156, 86];
export function winRateGlowStyle(v) {
  if (v == null || v < 90) return undefined;
  const ex = Math.min(1, (v - 90) / 10);
  if (ex <= 0) return undefined;
  const [r, g, b] = WINRATE_GLOW_RGB;
  const k = (mult) => (ex * mult).toFixed(3);
  return {
    boxShadow: [
      `0 0 0 1px rgba(${r},${g},${b},${k(0.3)})`,
      `inset 0 1px 0 0 rgba(${r},${g},${b},${k(0.1)})`,
      `0 0 6px rgba(${r},${g},${b},${k(0.48)})`,
      `0 0 14px rgba(${r},${g},${b},${k(0.3)})`,
      `0 0 26px rgba(${r},${g},${b},${k(0.16)})`,
    ].join(', '),
  };
}

export function captureColor(v) {
  if (v == null) return 'text-gray-600';
  if (v >= 0.5) return 'text-trading-green-400';
  if (v >= 0.3) return 'text-amber-300';
  return 'text-trading-red-400';
}

export function icColor(v) {
  if (v == null) return 'text-gray-600';
  if (v >= 0.1) return 'text-trading-green-400';
  if (v >= 0)   return 'text-amber-300';
  return 'text-trading-red-400';
}

export function corrColor(v) {
  if (v == null) return 'text-gray-600';
  if (v >= 0.1) return 'text-trading-green-400';
  if (v >= 0)   return 'text-amber-300';
  return 'text-trading-red-400';
}

export function rtrColor(v) {
  if (v == null) return 'text-gray-600';
  if (v > 20)  return 'text-trading-green-300';
  if (v > 0)   return 'text-trading-green-400';
  if (v > -20) return 'text-amber-300';
  return 'text-trading-red-400';
}

export function BucketPill({ bucket }) {
  const isCall = CALL_BUCKETS.has(bucket);
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${
      isCall ? 'text-trading-green-300' : 'text-trading-red-300'
    }`}>
      <span
        className={`w-1 h-1 rounded-full ${isCall ? 'bg-trading-green-300' : 'bg-trading-red-300'}`}
        aria-hidden="true"
      />
      {bucket}
    </span>
  );
}

export function DeltaBadge({ curr, prev, decimals = 1, suffix = '%', invert = false }) {
  if (curr == null || prev == null) return null;
  const d = curr - prev;
  const minDelta = decimals >= 3 ? 0.0005 : 0.05;
  if (Math.abs(d) < minDelta) return null;
  const good = invert ? d < 0 : d > 0;
  const label = fmtSignedMagnitude(d, { decimals, suffix, compactSuffix: 'k' });
  if (label == null) return null;
  return (
    <span className={`ml-1 font-mono tabular-nums text-[10px] ${good ? 'text-trading-green-400/80' : 'text-trading-red-400/80'}`}>
      ({label})
    </span>
  );
}

// Shared by the win_rate and take_profit tabs (colorFn/glowFn let take_profit
// swap in tpRateColor / a BE-anchored glow instead of the WR defaults).
export function WinRateTable({
  buckets, useUnscaled = false, prevByBucket = {}, primaryPeriods = [], colorFn = null, glowFn = null,
}) {
  const displayPeriods = ASSESSMENT_PERIODS;
  const primarySet = new Set(primaryPeriods);
  const cellColor = colorFn || ((v, _side) => winRateColor(v));
  const cellGlow  = glowFn  || ((v, _side) => winRateGlowStyle(v));
  return (
    <ScrollTable minWidth={520}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08]">
            <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-20">Bucket</th>
            <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-12">N</th>
            {displayPeriods.map(p => {
              const isPrimary = primarySet.has(p);
              return (
                <th key={p} className={`text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] min-w-[58px] ${
                  isPrimary ? 'text-trading-blue-300 bg-trading-blue-500/10' : 'text-gray-600'
                }`}>{p}</th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {buckets.map((row) => {
            const prev = prevByBucket[row.bucket];
            return (
            <tr
              key={row.bucket}
              className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
            >
              <td className="py-2 px-3"><BucketPill bucket={row.bucket} /></td>
              <td className="py-2 px-2 text-right text-gray-500 font-mono tabular-nums text-[11px] whitespace-nowrap">
                {row.sample_count}
                <DeltaBadge curr={row.sample_count} prev={prev?.sample_count} decimals={0} suffix="" />
              </td>
              {displayPeriods.map(p => {
                const vRaw = useUnscaled ? row.win_rate_unscaled?.[p] : row.win_rate?.[p];
                const pv   = useUnscaled ? prev?.win_rate_unscaled?.[p] : prev?.win_rate?.[p];
                const lowN = row.sample_count < MIN_RELIABLE_N;
                const v    = lowN ? null : vRaw;
                const glow = cellGlow(v, row.side);
                const isPrimary = primarySet.has(p);
                return (
                  <td key={p} className={`py-2 px-2 text-right align-middle whitespace-nowrap ${
                    isPrimary ? 'bg-trading-blue-500/[0.05]' : ''
                  }`}>
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded-md font-mono tabular-nums text-[12px] ${cellColor(v, row.side)}`}
                      style={glow}
                    >
                      {v != null ? `${v.toFixed(1)}%` : '—'}
                    </span>
                    <DeltaBadge curr={v} prev={pv} />
                  </td>
                );
              })}
            </tr>
          );
          })}
        </tbody>
      </table>
    </ScrollTable>
  );
}

export function SectionHeader({ children }) {
  return (
    <div className="mb-3">
      <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500">
        {children}
      </span>
    </div>
  );
}

// Scrollable table wrapper — ensures horizontal scroll on small screens
export function ScrollTable({ children, minWidth = 480 }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <div style={{ minWidth }}>
        {children}
      </div>
    </div>
  );
}
